// workspaceGroups.ts — the sidebar's two-level shape: workspaces, each
// holding the sessions that run on it.
//
// Pure, so the ordering rules are tested rather than eyeballed
// (workspaceGroups.test.ts). The Sessions page renders what this returns
// and nothing else decides order.

import { sessionRecency } from './chatState';

/** What a session needs to be filed and sorted. */
export interface GroupableSession {
  id: string;
  workspace_id?: string;
  last_message_at?: string;
  updated_at?: string;
}

/** What a workspace needs to head a group. */
export interface GroupableWorkspace {
  id: string;
  name: string;
  created_at?: string;
}

export interface WorkspaceGroup<S, W> {
  workspace: W;
  /** Most recently talked in first. */
  sessions: S[];
  /** What the group sorts by, in ms: its newest session, else its creation. */
  recency: number;
  /**
   * True when the workspace row has not arrived yet — a session created
   * in another tab can land a frame before its workspace's does. The
   * header renders, but without controls that need the row.
   */
  placeholder: boolean;
}

/** The workspace a session runs in; a pre-workspace session is its own. */
export function workspaceIdOf(session: GroupableSession): string {
  return session.workspace_id || session.id;
}

/**
 * File sessions under their workspaces.
 *
 * Groups are ordered by their most recently talked-in session, so the
 * workspace the user is working in stays at the top however many quiet
 * ones they have; a workspace with no sessions yet sorts by when it was
 * made, which puts one just created at the top too. Every workspace
 * appears, empty or not — an empty one is still a machine with files on
 * it, and the place to start its next session.
 */
export function groupByWorkspace<S extends GroupableSession, W extends GroupableWorkspace>(
  workspaces: W[],
  sessions: S[],
  placeholder: (id: string) => W,
): WorkspaceGroup<S, W>[] {
  const groups = new Map<string, WorkspaceGroup<S, W>>();
  for (const ws of workspaces) {
    const created = ws.created_at ? Date.parse(ws.created_at) : 0;
    groups.set(ws.id, {
      workspace: ws,
      sessions: [],
      recency: Number.isNaN(created) ? 0 : created,
      placeholder: false,
    });
  }
  const ordered = [...sessions].sort((a, b) => sessionRecency(b) - sessionRecency(a));
  for (const session of ordered) {
    const id = workspaceIdOf(session);
    let group = groups.get(id);
    if (!group) {
      group = { workspace: placeholder(id), sessions: [], recency: 0, placeholder: true };
      groups.set(id, group);
    }
    // A session is never older than the workspace it was made in, so
    // this is the newest session whenever there is one.
    group.recency = Math.max(group.recency, sessionRecency(session));
    group.sessions.push(session);
  }
  return [...groups.values()].sort((a, b) => b.recency - a.recency);
}
